import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  integrationsListIntegrations,
  organizationsListIntegrations,
  organizationsDescribeIntegration,
  organizationsListIntegrationResources,
  organizationsCreateIntegration,
  organizationsUpdateIntegration,
  organizationsDeleteIntegration,
  organizationsNextIntegrationSetupStep,
  organizationsPreviousIntegrationSetupStep,
  organizationsUpdateIntegrationSecret,
  organizationsUpdateIntegrationProperty,
  organizationsUpdateIntegrationCapabilities,
} from "@/api-client/sdk.gen";
import type {
  IntegrationCapabilityState,
  IntegrationsIntegrationDefinition,
  OrganizationsIntegration,
} from "@/api-client/types.gen";
import { withOrganizationHeader } from "@/lib/withOrganizationHeader";
import { getIntegrationTypeDisplayName } from "@/lib/integrationDisplayName";
import { useOrganizationId } from "@/hooks/useOrganizationId";

export const integrationKeys = {
  all: ["integrations"] as const,
  available: (organizationId?: string) => [...integrationKeys.all, "available", organizationId ?? ""] as const,
  connected: (organizationId: string) => [...integrationKeys.all, "connected", organizationId] as const,
  integration: (organizationId: string, integrationId: string) =>
    [...integrationKeys.connected(organizationId), integrationId] as const,
  resources: (
    organizationId: string,
    integrationId: string,
    resourceType: string,
    parameters?: Record<string, string>,
  ) =>
    [
      ...integrationKeys.integration(organizationId, integrationId),
      "resources",
      resourceType,
      Object.entries(parameters ?? {})
        .map(([k, v]) => `${k}=${v}`)
        .join("&"),
    ] as const,
};

// Hook to fetch available integrations (catalog).
// Normalizes each integration's label (e.g. "github" -> "GitHub") so consumers get correct display names.
export const useAvailableIntegrations = (options?: { enabled?: boolean; organizationId?: string }) => {
  const organizationIdFromRoute = useOrganizationId();
  const organizationId = options?.organizationId ?? organizationIdFromRoute ?? undefined;
  const query = useQuery({
    queryKey: integrationKeys.available(organizationId),
    queryFn: async () => {
      const response = await integrationsListIntegrations(withOrganizationHeader({ organizationId }));
      const list: IntegrationsIntegrationDefinition[] = response.data?.integrations || [];
      return {
        integrations: list.map((integration: IntegrationsIntegrationDefinition) => {
          // Support both camelCase and PascalCase (API may send either)
          const rawLabel = integration.label;
          const rawName = integration.name;
          const displayLabel = getIntegrationTypeDisplayName(rawLabel, rawName) || rawLabel || rawName || "";
          return { ...integration, label: displayLabel } as IntegrationsIntegrationDefinition;
        }),
        githubAppConfigured: response.data?.githubAppConfigured === true,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled ?? true,
  });

  return {
    ...query,
    data: query.data?.integrations,
    githubAppConfigured: query.data?.githubAppConfigured === true,
  };
};

// Hook to fetch connected integrations for an organization
export const useConnectedIntegrations = (organizationId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: integrationKeys.connected(organizationId),
    queryFn: async () => {
      const response = await organizationsListIntegrations(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId },
        }),
      );
      return response.data?.integrations || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId && (options?.enabled ?? true),
  });
};

// Hook to fetch a single integration
export const useIntegration = (organizationId: string, integrationId: string) => {
  return useQuery({
    queryKey: integrationKeys.integration(organizationId, integrationId),
    queryFn: async () => {
      const response = await organizationsDescribeIntegration(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId, integrationId },
        }),
      );
      return response.data?.integration || null;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId && !!integrationId,
  });
};

const DEFAULT_BRANCH_FALLBACK = "main";

/**
 * Imperative lookup of a single repository's default branch (main, master,
 * staging, ...), used by onboarding finish before installing the generated
 * automations. Falls back to "main" when the integration or repository is
 * missing, or the lookup returns nothing, so callers never block on this.
 */
export async function resolveGithubDefaultBranch(
  organizationId: string,
  integrationId: string,
  repository: string,
): Promise<string> {
  if (!organizationId || !integrationId || !repository) {
    return DEFAULT_BRANCH_FALLBACK;
  }

  const response = await organizationsListIntegrationResources(
    withOrganizationHeader({
      organizationId,
      path: { id: organizationId, integrationId },
      query: { type: "default_branch", repository },
    }),
  );
  const branch = response.data?.resources?.[0]?.name;
  return branch || DEFAULT_BRANCH_FALLBACK;
}

export const useIntegrationResources = (
  organizationId: string,
  integrationId: string,
  resourceType: string,
  parameters?: Record<string, string>,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: integrationKeys.resources(organizationId, integrationId, resourceType, parameters),
    queryFn: async () => {
      const query: Record<string, string> = {
        type: resourceType,
      };

      for (const [k, v] of Object.entries(parameters ?? {})) {
        query[k] = v;
      }

      const response = await organizationsListIntegrationResources(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId, integrationId },
          query,
        }),
      );
      return response.data?.resources || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(organizationId && integrationId && resourceType) && (options?.enabled ?? true),
  });
};

export type IntegrationSource = "integrations_page" | "node_configuration" | "install_wizard";

// Hook to create an integration
export const useCreateIntegration = (organizationId: string, _source: IntegrationSource) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      integrationName: string;
      name: string;
      configuration?: Record<string, unknown>;
      capabilities?: string[];
    }) => {
      return await organizationsCreateIntegration(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId },
          body: {
            integrationName: data.integrationName,
            name: data.name,
            configuration: data.configuration,
            capabilities: data.capabilities,
          },
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connected(organizationId),
      });
    },
  });
};

export const useNextIntegrationSetupStep = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { integrationId: string; inputs?: Record<string, unknown>; capabilities?: string[] }) => {
      return await organizationsNextIntegrationSetupStep(
        withOrganizationHeader({
          path: { id: organizationId, integrationId: data.integrationId },
          body: {
            inputs: data.inputs,
            capabilities: data.capabilities,
          },
        }),
      );
    },
    onSuccess: (response) => {
      const integration = response.data?.integration;
      const integrationId = integration?.metadata?.id;
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connected(organizationId),
      });

      if (integrationId) {
        queryClient.setQueryData(integrationKeys.integration(organizationId, integrationId), integration);
      }
    },
  });
};

export const usePreviousIntegrationSetupStep = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { integrationId: string }) => {
      return await organizationsPreviousIntegrationSetupStep(
        withOrganizationHeader({
          path: { id: organizationId, integrationId: data.integrationId },
          body: {},
        }),
      );
    },
    onSuccess: (response) => {
      const integration = response.data?.integration;
      const integrationId = integration?.metadata?.id;

      queryClient.invalidateQueries({
        queryKey: integrationKeys.connected(organizationId),
      });

      if (integrationId) {
        queryClient.setQueryData(integrationKeys.integration(organizationId, integrationId), integration);
      }
    },
  });
};

export const useUpdateIntegration = (organizationId: string, integrationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name?: string; configuration?: Record<string, unknown> }) => {
      return await organizationsUpdateIntegration(
        withOrganizationHeader({
          organizationId,
          path: { id: organizationId, integrationId },
          body: {
            name: data.name,
            configuration: data.configuration,
          },
        }),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connected(organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: integrationKeys.integration(organizationId, integrationId),
      });
    },
  });
};

export const useUpdateIntegrationCapabilities = (organizationId: string, integrationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (capabilities: IntegrationCapabilityState[]) => {
      return await organizationsUpdateIntegrationCapabilities(
        withOrganizationHeader({
          path: { id: organizationId, integrationId },
          body: { capabilities },
        }),
      );
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connected(organizationId),
      });
      const integration = response.data?.integration;
      if (integration) {
        queryClient.setQueryData(integrationKeys.integration(organizationId, integrationId), integration);
        return;
      }

      queryClient.invalidateQueries({
        queryKey: integrationKeys.integration(organizationId, integrationId),
      });
    },
  });
};

function applyIntegrationDescribeCache(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
  integrationId: string,
  integration: OrganizationsIntegration | null,
) {
  queryClient.invalidateQueries({
    queryKey: integrationKeys.connected(organizationId),
  });
  if (integration?.metadata?.id) {
    queryClient.setQueryData(integrationKeys.integration(organizationId, integration.metadata.id), integration);
    return;
  }

  queryClient.invalidateQueries({
    queryKey: integrationKeys.integration(organizationId, integrationId),
  });
}

export const useUpdateIntegrationProperty = (organizationId: string, integrationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { propertyName: string; value: string }) => {
      const response = await organizationsUpdateIntegrationProperty(
        withOrganizationHeader({
          path: { id: organizationId, integrationId },
          body: { propertyName: body.propertyName, value: body.value },
        }),
      );
      return response.data?.integration ?? null;
    },
    onSuccess: (integration) => {
      applyIntegrationDescribeCache(queryClient, organizationId, integrationId, integration);
    },
  });
};

export const useUpdateIntegrationSecret = (organizationId: string, integrationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { secretName: string; value: string }) => {
      const response = await organizationsUpdateIntegrationSecret(
        withOrganizationHeader({
          path: { id: organizationId, integrationId },
          body: { secretName: body.secretName, value: body.value },
        }),
      );
      return response.data?.integration ?? null;
    },
    onSuccess: (integration) => {
      applyIntegrationDescribeCache(queryClient, organizationId, integrationId, integration);
    },
  });
};

export const useIntegrationMutations = (organizationId: string, integrationId: string) => {
  const deleteMutation = useDeleteIntegration(organizationId, integrationId);
  const updateCapabilitiesMutation = useUpdateIntegrationCapabilities(organizationId, integrationId);
  const updatePropertyMutation = useUpdateIntegrationProperty(organizationId, integrationId);
  const updateSecretMutation = useUpdateIntegrationSecret(organizationId, integrationId);

  return {
    deleteMutation,
    updateCapabilitiesMutation,
    updatePropertyMutation,
    updateSecretMutation,
    settingsMutationBusy: updatePropertyMutation.isPending || updateSecretMutation.isPending,
  };
};

// Hook to delete an integration
export const useDeleteIntegration = (organizationId: string, integrationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { integrationName: string }) => {
      await organizationsDeleteIntegration(
        withOrganizationHeader({
          path: { id: organizationId, integrationId },
        }),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.connected(organizationId),
      });
      queryClient.removeQueries({
        queryKey: integrationKeys.integration(organizationId, integrationId),
      });
    },
  });
};
